import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Flex,
  Box,
  Input,
  Image,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogBackdrop,
  DialogPositioner,
  Portal,
  Skeleton,
  Spinner,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuUpload, LuSearch, LuX, LuArrowLeft } from 'react-icons/lu';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { searchStockImages, type StockImage } from '../api/stockImages';

function getCroppedImg(imageSrc: string, cropArea: Area): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('No 2d context')); return; }

      canvas.width = cropArea.width;
      canvas.height = cropArea.height;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        image,
        cropArea.x, cropArea.y, cropArea.width, cropArea.height,
        0, 0, cropArea.width, cropArea.height,
      );
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/jpeg', 0.9);
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
}

interface ImagePickerModalProps {
  open: boolean;
  onClose: () => void;
  onImageReady: (blob: Blob) => Promise<void>;
  aspectRatio?: number;
}

const DEBOUNCE_MS = 400;
const PAGE_SIZE = 9;
const SKELETON_COUNT = 9;

const ImagePickerModal = ({ open, onClose, onImageReady, aspectRatio = 1 }: ImagePickerModalProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'stock' | 'upload'>('upload');

  // Stock images state
  const [images, setImages] = useState<StockImage[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);

  // Crop state (step 2)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset on query change
  useEffect(() => {
    setPage(1);
    setImages([]);
  }, [debouncedQuery]);

  // Fetch stock images
  useEffect(() => {
    if (!open || activeTab !== 'stock' || cropImageSrc) return;
    const fetchImages = async () => {
      setIsLoading(true);
      try {
        const params: Record<string, string | number> = { page, page_size: PAGE_SIZE };
        if (debouncedQuery.trim()) params.query = debouncedQuery.trim();
        const data = await searchStockImages(params);
        if (page === 1) {
          setImages(data.images);
        } else {
          setImages(prev => [...prev, ...data.images]);
        }
        setTotal(data.total);
      } catch (error) {
        console.error('Failed to load stock images:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchImages();
  }, [open, activeTab, page, debouncedQuery, cropImageSrc]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setActiveTab('upload');
      setSearchQuery('');
      setDebouncedQuery('');
      setPage(1);
      setIsDragging(false);
      setCropImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } else {
      setImages([]);
    }
  }, [open]);

  const hasMore = images.length < total;

  // Infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setPage(p => p + 1);
        }
      },
      { root: container, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading]);

  const goToCrop = (dataUrl: string) => {
    setCropImageSrc(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const goBack = () => {
    if (!isProcessing) {
      setCropImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  };

  const handleFileChange = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      goToCrop(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileChange(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileChange(file);
    }
  };

  const handleStockImageSelect = async (image: StockImage) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = () => {
        goToCrop(reader.result as string);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to load stock image:', error);
    }
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropConfirm = async () => {
    if (!croppedAreaPixels || !cropImageSrc) return;
    setIsProcessing(true);
    try {
      const blob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      await onImageReady(blob);
      onClose();
    } catch (error) {
      console.error('Crop failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  // Step 2: Crop view
  if (cropImageSrc) {
    return (
      <DialogRoot open={open} onOpenChange={(e) => !e.open && handleClose()} placement="center">
        <Portal>
          <DialogBackdrop bg="blackAlpha.600" />
          <DialogPositioner>
            <DialogContent
              backgroundColor="neutral.800"
              borderWidth="1px"
              borderColor="neutral.700"
              borderRadius="8px"
              maxW="520px"
              width="100%"
              m={{ base: '16px', md: '0px' }}
              p={{ base: '20px', md: '24px' }}
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <DialogHeader pb="0" flexShrink={0}>
                <Flex alignItems="center" gap="8px">
                  <Box
                    as="button"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="32px"
                    height="32px"
                    borderRadius="6px"
                    cursor={isProcessing ? 'not-allowed' : 'pointer'}
                    color="neutral.400"
                    backgroundColor="transparent"
                    _hover={isProcessing ? {} : { backgroundColor: 'neutral.700', color: 'neutral.200' }}
                    onClick={goBack}
                    aria-disabled={isProcessing || undefined}
                  >
                    <LuArrowLeft size={18} />
                  </Box>
                  <DialogTitle fontSize="20px" fontWeight="500" color="neutral.100">
                    {t('menus.cropImage', 'Crop Image')}
                  </DialogTitle>
                </Flex>
                <Box
                  as="button"
                  position="absolute"
                  top="20px"
                  right="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="32px"
                  height="32px"
                  borderRadius="6px"
                  cursor={isProcessing ? 'not-allowed' : 'pointer'}
                  color="neutral.400"
                  backgroundColor="transparent"
                  _hover={isProcessing ? {} : { backgroundColor: 'neutral.700', color: 'neutral.200' }}
                  onClick={handleClose}
                  aria-disabled={isProcessing || undefined}
                >
                  <LuX size={18} />
                </Box>
              </DialogHeader>

              <DialogBody py="16px" overflow="hidden" display="flex" flexDirection="column" flex="1">
                <Box
                  position="relative"
                  width="100%"
                  height="350px"
                  borderRadius="8px"
                  overflow="hidden"
                >
                  <Cropper
                    image={cropImageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspectRatio}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    minZoom={1}
                    maxZoom={3}
                    objectFit="contain"
                    style={{
                      containerStyle: { borderRadius: '8px' },
                      cropAreaStyle: { border: '2px solid white' },
                    }}
                  />
                  {isProcessing && (
                    <Flex
                      position="absolute"
                      inset="0"
                      justifyContent="center"
                      alignItems="center"
                      backgroundColor="blackAlpha.600"
                      borderRadius="8px"
                      zIndex={10}
                    >
                      <Spinner size="lg" color="neutral.100" />
                    </Flex>
                  )}
                </Box>

                <Flex alignItems="center" gap="12px" mt="16px">
                  <Box fontSize="13px" color="neutral.400" flexShrink={0}>Zoom</Box>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    disabled={isProcessing}
                    className="crop-zoom-slider"
                  />
                </Flex>

                <Box fontSize="13px" color="neutral.400" mt="8px" textAlign="center">
                  Scroll to zoom, drag to reposition
                </Box>

                <Flex justifyContent="flex-end" gap="12px" mt="16px">
                  <Button
                    type="button"
                    height="fit-content"
                    py="8px"
                    px="16px"
                    fontWeight="500"
                    fontSize="14px"
                    color="neutral.300"
                    backgroundColor="transparent"
                    borderWidth="1px"
                    borderColor="neutral.600"
                    borderRadius="6px"
                    onClick={goBack}
                    disabled={isProcessing}
                    _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
                  >
                    {t('common.back', 'Back')}
                  </Button>
                  <Button
                    type="button"
                    height="fit-content"
                    py="8px"
                    px="16px"
                    fontWeight="500"
                    fontSize="14px"
                    color="neutral.900"
                    backgroundColor="neutral.100"
                    borderRadius="6px"
                    onClick={handleCropConfirm}
                    disabled={!croppedAreaPixels || isProcessing}
                    _hover={{ backgroundColor: 'neutral.200' }}
                  >
                    {isProcessing ? (
                      <Flex alignItems="center" gap="8px">
                        <Spinner size="sm" color="neutral.900" />
                        <span>{t('common.uploading', 'Uploading...')}</span>
                      </Flex>
                    ) : (
                      t('menus.applyCrop', 'Apply Crop')
                    )}
                  </Button>
                </Flex>
              </DialogBody>
            </DialogContent>
          </DialogPositioner>
        </Portal>

        <style>{`
          .reactEasyCrop_CropArea {
            border: 2px solid white !important;
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5) !important;
          }
          .crop-zoom-slider {
            width: 100%;
            -webkit-appearance: none;
            appearance: none;
            height: 4px;
            background: #383838;
            border-radius: 2px;
            outline: none;
          }
          .crop-zoom-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 16px;
            height: 16px;
            background: #EFF2F6;
            border-radius: 50%;
            cursor: pointer;
          }
          .crop-zoom-slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            background: #EFF2F6;
            border-radius: 50%;
            cursor: pointer;
            border: none;
          }
          .crop-zoom-slider:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}</style>
      </DialogRoot>
    );
  }

  // Step 1: Picker view
  return (
    <DialogRoot open={open} onOpenChange={(e) => !e.open && onClose()} placement="center">
      <Portal>
        <DialogBackdrop bg="blackAlpha.600" />
        <DialogPositioner>
          <DialogContent
            backgroundColor="neutral.800"
            borderWidth="1px"
            borderColor="neutral.700"
            borderRadius="8px"
            maxW="520px"
            width="100%"
            maxH="85vh"
            m={{ base: '16px', md: '0px' }}
            p={{ base: '20px', md: '24px' }}
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <DialogHeader pb="0" flexShrink={0}>
              <DialogTitle fontSize="20px" fontWeight="500" color="neutral.100">
                {t('menus.chooseImage', 'Choose Image')}
              </DialogTitle>
              <Box
                as="button"
                position="absolute"
                top="20px"
                right="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="32px"
                height="32px"
                borderRadius="6px"
                cursor="pointer"
                color="neutral.400"
                backgroundColor="transparent"
                _hover={{ backgroundColor: 'neutral.700', color: 'neutral.200' }}
                onClick={onClose}
              >
                <LuX size={18} />
              </Box>
            </DialogHeader>

            <DialogBody py="16px" overflow="hidden" display="flex" flexDirection="column" flex="1">
              {/* Tabs */}
              <Flex mb="16px" gap="0" borderBottomWidth="1px" borderColor="neutral.700" flexShrink={0}>
                <Tab active={activeTab === 'upload'} onClick={() => setActiveTab('upload')}>
                  {t('menus.upload', 'Upload')}
                </Tab>
                <Tab active={activeTab === 'stock'} onClick={() => setActiveTab('stock')}>
                  {t('menus.stockImages', 'Stock Images')}
                </Tab>
              </Flex>

              {activeTab === 'upload' ? (
                <Flex
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  py="48px"
                  px="24px"
                  borderRadius="8px"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={isDragging ? 'neutral.400' : 'neutral.600'}
                  backgroundColor={isDragging ? 'neutral.600' : 'neutral.700'}
                  cursor="pointer"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.600' }}
                >
                  <Box p="12px" borderRadius="12px" backgroundColor="neutral.600" mb="12px">
                    <LuUpload size={24} color="#CFD5DE" />
                  </Box>
                  <Box fontSize="14px" fontWeight="500" color="neutral.300" mb="4px">
                    {t('menus.clickOrDragToUpload', 'Click or drag image to upload')}
                  </Box>
                  <Box fontSize="12px" color="neutral.500">
                    PNG, JPG, WebP
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileInputChange}
                  />
                </Flex>
              ) : (
                <Flex direction="column" gap="12px" flex="1" overflow="hidden">
                  {/* Search */}
                  <Flex position="relative" flexShrink={0}>
                    <Input
                      placeholder={t('menus.searchImages', 'Search images...')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      p="8px 12px"
                      pl="36px"
                      fontWeight="400"
                      fontSize="14px"
                      color="neutral.300"
                      borderWidth="1px"
                      borderColor="neutral.700"
                      backgroundColor="neutral.900"
                      borderRadius="8px"
                      _focus={{ borderColor: 'neutral.600', outline: 'none' }}
                    />
                    <Box
                      position="absolute"
                      left="12px"
                      top="50%"
                      transform="translateY(-50%)"
                      color="neutral.500"
                      pointerEvents="none"
                    >
                      <LuSearch size={14} />
                    </Box>
                  </Flex>

                  {/* Image Grid */}
                  {isLoading && images.length === 0 ? (
                    <Box flex="1" overflowY="auto" css={{ scrollbarWidth: 'thin' }}>
                      <Flex flexWrap="wrap" gap="8px">
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                          <Box key={i} width="calc(33.333% - 6px)" borderRadius="8px" overflow="hidden">
                            <Skeleton height="100px" width="100%" />
                            <Box px="6px" py="4px" backgroundColor="neutral.900">
                              <Skeleton height="14px" width="70%" />
                            </Box>
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  ) : images.length === 0 && !isLoading ? (
                    <Flex justifyContent="center" py="40px">
                      <Box fontSize="14px" color="neutral.400">
                        {t('menus.noStockImages', 'No images found')}
                      </Box>
                    </Flex>
                  ) : (
                    <Box ref={scrollRef} flex="1" overflowY="auto" css={{ scrollbarWidth: 'thin' }}>
                      <Flex flexWrap="wrap" gap="8px">
                        {images.map((img) => (
                          <Box
                            key={img.id}
                            width="calc(33.333% - 6px)"
                            cursor="pointer"
                            borderRadius="8px"
                            overflow="hidden"
                            borderWidth="2px"
                            borderColor="transparent"
                            transition="border-color 0.15s"
                            _hover={{ borderColor: 'neutral.400' }}
                            onClick={() => handleStockImageSelect(img)}
                          >
                            <Image
                              src={img.url}
                              alt={img.name}
                              width="100%"
                              height="100px"
                              objectFit="cover"
                            />
                            <Box px="6px" py="4px" backgroundColor="neutral.900">
                              <Box fontSize="11px" color="neutral.400" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                {img.name}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Flex>
                      {hasMore && isLoading && (
                        <Flex flexWrap="wrap" gap="8px" mt="8px">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <Box key={i} width="calc(33.333% - 6px)" borderRadius="8px" overflow="hidden">
                              <Skeleton height="100px" width="100%" />
                              <Box px="6px" py="4px" backgroundColor="neutral.900">
                                <Skeleton height="14px" width="70%" />
                              </Box>
                            </Box>
                          ))}
                        </Flex>
                      )}
                      <Box ref={sentinelRef} height="1px" />
                    </Box>
                  )}
                </Flex>
              )}
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};

const Tab = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <Box
    px="16px"
    py="10px"
    fontSize="14px"
    fontWeight="500"
    color={active ? 'neutral.100' : 'neutral.500'}
    borderBottomWidth="2px"
    borderColor={active ? 'neutral.100' : 'transparent'}
    cursor="pointer"
    transition="all 0.15s"
    _hover={{ color: active ? 'neutral.100' : 'neutral.300' }}
    onClick={onClick}
    marginBottom="-1px"
  >
    {children}
  </Box>
);

export default ImagePickerModal;
